const upper = (value) => String(value || '').toUpperCase();

const translated = (t, key, fallback = '') => t(key, { defaultValue: fallback || key });

export function localizeFacePersona(persona, t) {
    if (!persona) return persona;

    const personaId = upper(persona.persona_id || persona.id) || 'UNKNOWN';
    const baseType = upper(persona.base_type);
    const spiceType = upper(persona.spice_type);
    const report = persona.analysis_report || {};
    const basePrimary = upper(report.base_primary || persona.base_type);
    const baseSecondary = upper(report.base_secondary);
    const spicePrimary = upper(report.spice_primary || persona.spice_type);
    const spiceSecondary = upper(report.spice_secondary);
    const baseCommentKey = upper(report.base_comment_key || 'PRIMARY_ONLY');
    const spiceCommentKey = upper(report.spice_comment_key || 'PRIMARY_ONLY');
    const approachScore = Number(persona.approachability?.score);
    const inferredApproachKey = approachScore >= 60 ? 'EASY' : approachScore <= 40 ? 'HARD' : 'FLEXIBLE';
    const approachTypeKey = upper(persona.approachability?.type_key || inferredApproachKey);
    const approachDescKey = upper(persona.approachability?.desc_key || approachTypeKey);
    const bioDescKey = upper(persona.biological_impression?.desc_key
        || (Number(persona.biological_impression?.baby_schema) > 60 ? 'SOFT' : 'PRESENCE'));
    const actionPlan = report.action_plan || {};

    const baseParams = {
        primary: translated(t, `persona_traits.base.${basePrimary}`, basePrimary),
        secondary: translated(t, `persona_traits.base.${baseSecondary}`, baseSecondary),
    };
    const spiceParams = {
        primary: translated(t, `persona_traits.spice.${spicePrimary}`, spicePrimary),
        secondary: translated(t, `persona_traits.spice.${spiceSecondary}`, spiceSecondary),
    };

    const localizeEvidence = (item = {}) => {
        const featureId = upper(item.feature_id || item.feature).replace(/\s+/g, '_').replace(/-/g, '_');
        const direction = upper(item.direction || item.level || item.type || 'HIGH');
        const evidenceKey = upper(item.evidence_key || `${featureId}_${direction}`);
        return {
            ...item,
            feature_id: featureId,
            direction,
            evidence_key: evidenceKey,
            feature: translated(t, `evidence.${evidenceKey}.title`, item.feature),
            desc: translated(t, `evidence.${evidenceKey}.description`, item.desc),
            comment: t(`evidence.${evidenceKey}.comment`, {
                score: Number(item.score || 0).toFixed(1),
                contribution: Number(item.contribution || 0).toFixed(1),
                defaultValue: item.comment || '',
            }),
            type: direction === 'HIGH' ? 'High' : direction === 'LOW' ? 'Low' : 'Balanced',
        };
    };
    const evidence = (Array.isArray(report.evidence) ? report.evidence : []).map(localizeEvidence);
    const explanation = report.explanation || {};
    const fallbackEvidenceByBase = {
        NOBLE: { feature_id: 'FACE_ASPECT_RATIO', direction: 'HIGH', evidence_key: 'FACE_ASPECT_RATIO_HIGH' },
        CUTE: { feature_id: 'LOWER_FACE_RATIO', direction: 'LOW', evidence_key: 'LOWER_FACE_RATIO_LOW' },
        STRONG: { feature_id: 'JAW_ANGLE', direction: 'LOW', evidence_key: 'JAW_ANGLE_LOW' },
        BALANCE: { feature_id: 'FACE_ASPECT_RATIO', direction: 'BALANCED', evidence_key: 'FACE_ASPECT_RATIO_BALANCED' },
    };
    const fallbackEvidence = localizeEvidence(fallbackEvidenceByBase[baseType] || fallbackEvidenceByBase.BALANCE);
    const strongestFeature = localizeEvidence(explanation.strongest_feature || evidence[0] || fallbackEvidence);
    const secondStrongestFeature = localizeEvidence(explanation.second_strongest_feature || evidence[1] || evidence[0]);
    const weakestFeature = localizeEvidence(explanation.weakest_feature || evidence[evidence.length - 1] || evidence[0]);
    const firstImpressionKey = upper(explanation.first_impression_key || strongestFeature.direction || 'HIGH');
    const dominantScore = Number(explanation.dominant_score ?? Math.max(
        ...Object.values(persona.scores?.base || {}).map(Number),
        ...Object.values(persona.scores?.spice || {}).map(Number),
        0,
    )).toFixed(1);
    const firstImpressionParams = {
        base: baseParams.primary,
        spice: spiceParams.primary,
        evidence: strongestFeature.feature || strongestFeature.desc,
        secondEvidence: secondStrongestFeature.feature || secondStrongestFeature.desc,
        score: dominantScore,
    };

    const hairKey = upper(actionPlan.hair_key || `${spiceType}_${baseType}`);
    const fashionKey = upper(actionPlan.fashion_key || spiceType);
    const itemKey = upper(actionPlan.item_key || spiceType);
    const tipKey = upper(actionPlan.tip_key || spiceType);

    return {
        ...persona,
        id: personaId,
        persona_id: personaId,
        title: translated(t, `persona.${personaId}.title`, persona.title),
        killer_copy: translated(t, `persona.${personaId}.killer_copy`, persona.killer_copy),
        description: translated(t, `persona.${personaId}.description`, persona.description),
        flirting_tip: translated(t, `persona.${personaId}.flirting_tip`, persona.flirting_tip),
        biological_impression: {
            ...(persona.biological_impression || {}),
            desc: translated(t, `biological_impression.${bioDescKey}.description`, persona.biological_impression?.desc),
        },
        approachability: {
            ...(persona.approachability || {}),
            type: translated(t, `approachability.${approachTypeKey}.title`, persona.approachability?.type),
            desc: translated(t, `approachability.${approachDescKey}.description`, persona.approachability?.desc),
        },
        analysis_report: {
            ...report,
            base_comment: t(`analysis_report.base.${baseCommentKey}`, { ...baseParams, defaultValue: report.base_comment || '' }),
            spice_comment: t(`analysis_report.spice.${spiceCommentKey}`, { ...spiceParams, defaultValue: report.spice_comment || '' }),
            gap_comment: translated(t, `analysis_report.gap.${upper(report.gap_comment_key || 'KEEP_NATURAL')}`, report.gap_comment),
            first_impression: t(`analysis_report.first_impression.${firstImpressionKey}`, firstImpressionParams),
            evidence_summary: t('analysis_report.first_impression.EVIDENCE_PAIR', firstImpressionParams),
            evidence,
            explanation: {
                ...explanation,
                dominant_score: dominantScore,
                strongest_feature: strongestFeature,
                second_strongest_feature: secondStrongestFeature,
                weakest_feature: weakestFeature,
            },
            action_plan: {
                ...actionPlan,
                hair: translated(t, `action_plan.hair.${spiceType}`, actionPlan.hair || translated(t, `action_plan.hair.${hairKey}`)),
                fashion: translated(t, `action_plan.fashion.${fashionKey}`, actionPlan.fashion),
                item: translated(t, `action_plan.item.${itemKey}`, actionPlan.item),
                tip: translated(t, `action_plan.tip.${tipKey}`, actionPlan.tip),
            },
        },
    };
}
